import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Briefcase, TrendingUp, Target, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface PipelineKpis {
  deals_total: number;
  deals_won: number;
  deals_lost: number;
  deals_open: number;
  ca_won_eur: number;
  pipeline_value_eur: number;
  objective_2026_eur: number;
  progress_pct: number;
  conversion_rate_pct: number;
  by_stage: Array<{ stage: string; label: string; count: number; value: number }>;
}

interface TopDeal {
  id: string;
  nom: string;
  etape: string;
  etape_label: string;
  montant: number;
  cloture: string;
}

const fmtEur = (n: number) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M€`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k€`;
  return `${n.toFixed(0)}€`;
};

export function LivePipelineWidget() {
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const [kpis, setKpis] = useState<PipelineKpis | null>(null);
  const [topDeals, setTopDeals] = useState<TopDeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchedAt, setFetchedAt] = useState<string>("");

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await fetch(`${BACKEND}/api/external/pipeline`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setKpis(d.kpis);
        setTopDeals(d.top_open_deals || []);
        setFetchedAt(d.fetched_at || "");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 60000); // refresh every 1 min
    return () => clearInterval(i);
  }, []);

  if (loading) {
    return (
      <Card data-testid="live-pipeline-widget">
        <CardContent className="py-10 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement du pipeline live…
        </CardContent>
      </Card>
    );
  }

  if (!kpis) return null;

  return (
    <Card data-testid="live-pipeline-widget" className="border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-transparent">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-lg flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-amber-400" />
            Pipeline HubSpot — Live
            <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px]">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse mr-1" />
              Live
            </Badge>
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={load} disabled={refreshing} data-testid="pipeline-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Big KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-muted/20 rounded-lg p-3">
            <div className="text-xs text-muted-foreground">Pipeline ouvert</div>
            <div className="text-2xl font-bold text-amber-400" data-testid="kpi-pipeline-value">
              {fmtEur(kpis.pipeline_value_eur)}
            </div>
            <div className="text-[11px] text-muted-foreground">{kpis.deals_open} deals</div>
          </div>
          <div className="bg-muted/20 rounded-lg p-3">
            <div className="text-xs text-muted-foreground">CA encaissé (won)</div>
            <div className="text-2xl font-bold text-emerald-400" data-testid="kpi-ca-won">
              {fmtEur(kpis.ca_won_eur)}
            </div>
            <div className="text-[11px] text-muted-foreground">{kpis.deals_won} gagnés</div>
          </div>
          <div className="bg-muted/20 rounded-lg p-3">
            <div className="text-xs text-muted-foreground">Total deals</div>
            <div className="text-2xl font-bold">{kpis.deals_total}</div>
            <div className="text-[11px] text-muted-foreground">{kpis.conversion_rate_pct}% conversion</div>
          </div>
          <div className="bg-muted/20 rounded-lg p-3">
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Target className="h-3 w-3" /> Objectif 2026
            </div>
            <div className="text-2xl font-bold text-violet-300">{fmtEur(kpis.objective_2026_eur)}</div>
            <div className="text-[11px] text-muted-foreground">{kpis.progress_pct}% atteint</div>
          </div>
        </div>

        {/* Progress bar towards 1M€ */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-xs font-medium flex items-center gap-1.5">
              <TrendingUp className="h-3.5 w-3.5 text-primary" />
              Progression vers 1M€
            </div>
            <div className="text-xs text-muted-foreground">
              {fmtEur(kpis.ca_won_eur)} / {fmtEur(kpis.objective_2026_eur)}
            </div>
          </div>
          <Progress value={Math.min(kpis.progress_pct, 100)} data-testid="progress-objective" />
        </div>

        {/* By stage */}
        {kpis.by_stage.length > 0 && (
          <div>
            <div className="text-xs font-medium mb-2">Répartition par étape</div>
            <div className="space-y-1.5">
              {kpis.by_stage.map((s) => (
                <div
                  key={s.stage}
                  className="flex items-center justify-between gap-2 text-xs"
                  data-testid={`stage-${s.stage}`}
                >
                  <span className="flex-1 min-w-0 truncate">
                    {s.label} <span className="text-muted-foreground">({s.count})</span>
                  </span>
                  <span className="text-amber-400 font-mono">{fmtEur(s.value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top open deals */}
        {topDeals.length > 0 && (
          <div className="border-t border-border/40 pt-4">
            <div className="text-xs font-medium mb-2">Top 5 deals ouverts</div>
            <div className="space-y-1.5">
              {topDeals.map((d) => (
                <div
                  key={d.id}
                  className="flex items-center justify-between gap-2 text-xs py-1.5 border-b border-border/20 last:border-0"
                  data-testid={`top-deal-${d.id}`}
                >
                  <span className="flex-1 min-w-0 truncate">{d.nom}</span>
                  <Badge variant="outline" className="text-[10px]">
                    {d.etape_label}
                  </Badge>
                  <span className="font-mono text-amber-400 w-16 text-right">{fmtEur(d.montant)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {fetchedAt && (
          <p className="text-[10px] text-muted-foreground text-right">
            Source : HubSpot live · {new Date(fetchedAt).toLocaleString("fr-FR")}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
